import type {GeneratedScript} from '@contentlab/api/avatars/types';
import type {
  AvatarSpecConfig,
  DraftAiAvatarProject,
} from '@contentlab/stores/contentLabStore';
import {
  AvatarSize,
  DEFAULT_AVATAR_AUDIO_REDUCTION_FACTOR,
  DEFAULT_AVATAR_SPECS_CONFIG,
  DEFAULT_BACKGROUND_AUDIO_REDUCTION_FACTOR,
} from '@contentlab/stores/contentLabStore';
import {AvatarKind} from '@contentlab/types/aiAvatars';
import {ElementPosition} from '@contentlab/types/elementPosition';
import {
  AiAvatarDraft,
  AiAvatarScriptDraft,
} from '@contentlab/types/validatedDraft';
import {RgbaColor} from '@/types/rgbaColor';
import {DEFAULT_SCRIPT_CONFIG} from '../constants';

// ============================================================================
// AVATAR SPEC UTILITIES
// ============================================================================

// ----------------------------------------------------------------------------
// Avatar Spec: API → Store
// ----------------------------------------------------------------------------

/**
 * Normalizes an unknown config object into a valid AvatarSpecConfig.
 * Handles both API format (unknown) and store format (partial AvatarSpecConfig).
 * Uses DEFAULT_AVATAR_SPECS_CONFIG as fallback for missing or invalid values.
 */
export const normalizeAvatarSpecConfig = (
  config: unknown
): AvatarSpecConfig => {
  const defaultConfig = DEFAULT_AVATAR_SPECS_CONFIG();

  if (!config || typeof config !== 'object') {
    return defaultConfig;
  }

  const c = config as Record<string, unknown>;

  // Normalize avatarColor
  let {avatarColor} = defaultConfig;
  if (c.avatarColor && typeof c.avatarColor === 'object') {
    const color = c.avatarColor as Record<string, unknown>;
    if (
      typeof color.r === 'number' &&
      typeof color.g === 'number' &&
      typeof color.b === 'number' &&
      typeof color.a === 'number'
    ) {
      avatarColor = {
        r: color.r,
        g: color.g,
        b: color.b,
        a: color.a,
      } as RgbaColor;
    }
  }

  // Normalize avatarSize
  let {avatarSize} = defaultConfig;
  if (
    c.avatarSize &&
    (c.avatarSize === AvatarSize.SMALL ||
      c.avatarSize === AvatarSize.MEDIUM ||
      c.avatarSize === AvatarSize.LARGE)
  ) {
    avatarSize = c.avatarSize as AvatarSize;
  }

  // Normalize avatarPosition
  let {avatarPosition} = defaultConfig;
  if (
    c.avatarPosition &&
    Object.values(ElementPosition).includes(c.avatarPosition as ElementPosition)
  ) {
    avatarPosition = c.avatarPosition as ElementPosition;
  }

  return {
    removeBackground:
      typeof c.removeBackground === 'boolean'
        ? c.removeBackground
        : defaultConfig.removeBackground,
    circleMask:
      typeof c.circleMask === 'boolean'
        ? c.circleMask
        : defaultConfig.circleMask,
    cropAvatar:
      typeof c.cropAvatar === 'boolean'
        ? c.cropAvatar
        : defaultConfig.cropAvatar,
    avatarColor,
    avatarSize,
    avatarPosition,
  };
};

/**
 * Extracts avatar spec fields from an API response format.
 * The avatarSpec comes back in the format sent by getAiAvatarDraft:
 * - For LIBRARY: { type: 'library', libraryId: string, config: ... }
 * - For CUSTOM: { type: 'custom', imageAssetId: string, voiceId: string, config: ... }
 * - For VOICE_ONLY: { type: 'voice_only', voiceId: string }
 */
export const parseAvatarSpecFromApi = (
  avatarSpec: Partial<DraftAiAvatarProject['avatarSpec']>
): {
  selectedAvatarKind: AvatarKind | null;
  libraryId: string | null;
  imageAssetId: string | null;
  voiceIdVoiceOnly: string | null;
  voiceIdCustom: string | null;
  avatarAudioReductionFactor: number;
  backgroundAudioReductionFactor: number;
  configLibrary: AvatarSpecConfig | undefined;
  configCustom: AvatarSpecConfig | undefined;
} => {
  let selectedAvatarKind: AvatarKind | null = null;
  let libraryId: string | null = null;
  let imageAssetId: string | null = null;
  let voiceIdVoiceOnly: string | null = null;
  let voiceIdCustom: string | null = null;
  let avatarAudioReductionFactor = DEFAULT_AVATAR_AUDIO_REDUCTION_FACTOR;
  let backgroundAudioReductionFactor =
    DEFAULT_BACKGROUND_AUDIO_REDUCTION_FACTOR;
  let configLibrary: AvatarSpecConfig | undefined;
  let configCustom: AvatarSpecConfig | undefined;

  if (!avatarSpec || typeof avatarSpec !== 'object') {
    return {
      selectedAvatarKind,
      libraryId,
      imageAssetId,
      voiceIdVoiceOnly,
      voiceIdCustom,
      avatarAudioReductionFactor,
      backgroundAudioReductionFactor,
      configLibrary,
      configCustom,
    };
  }

  const spec = avatarSpec as {
    type?: string;
    libraryId?: string | null;
    imageAssetId?: string | null;
    voiceId?: string | null;
    avatarAudioReductionFactor?: number;
    backgroundAudioReductionFactor?: number;
    config?: unknown;
  };

  const specType = spec.type;
  const specWithConfig = spec as {config?: unknown};
  const config = specWithConfig.config
    ? normalizeAvatarSpecConfig(specWithConfig.config)
    : undefined;

  // Parse audio reduction factors from API (camelCase) with defaults
  if (
    typeof spec.avatarAudioReductionFactor === 'number' &&
    spec.avatarAudioReductionFactor >= 0 &&
    spec.avatarAudioReductionFactor <= 1
  ) {
    avatarAudioReductionFactor = spec.avatarAudioReductionFactor;
  }
  if (
    typeof spec.backgroundAudioReductionFactor === 'number' &&
    spec.backgroundAudioReductionFactor >= 0 &&
    spec.backgroundAudioReductionFactor <= 1
  ) {
    backgroundAudioReductionFactor = spec.backgroundAudioReductionFactor;
  }

  // Determine avatar kind: use type if provided, otherwise infer from fields
  if (specType === AvatarKind.LIBRARY || spec.libraryId) {
    selectedAvatarKind = AvatarKind.LIBRARY;
    libraryId = spec.libraryId || null;
    configLibrary = config;
  } else if (specType === AvatarKind.CUSTOM || spec.imageAssetId) {
    selectedAvatarKind = AvatarKind.CUSTOM;
    imageAssetId = spec.imageAssetId || null;
    voiceIdCustom = spec.voiceId || null;
    configCustom = config;
  } else if (specType === AvatarKind.VOICE_ONLY || spec.voiceId) {
    selectedAvatarKind = AvatarKind.VOICE_ONLY;
    voiceIdVoiceOnly = spec.voiceId || null;
  }

  return {
    selectedAvatarKind,
    libraryId,
    imageAssetId,
    voiceIdVoiceOnly,
    voiceIdCustom,
    avatarAudioReductionFactor,
    backgroundAudioReductionFactor,
    configLibrary,
    configCustom,
  };
};

// ----------------------------------------------------------------------------
// Avatar Spec: Store → API
// ----------------------------------------------------------------------------

/**
 * Gets the effective config for a given avatar kind.
 * Returns the mode-specific config if available, otherwise falls back to base config.
 */
export const getEffectiveAvatarConfig = (
  selectedAvatarKind: AvatarKind | null,
  avatarSpec: {
    config: AvatarSpecConfig;
    configLibrary?: AvatarSpecConfig;
    configCustom?: AvatarSpecConfig;
  }
): AvatarSpecConfig => {
  if (selectedAvatarKind === AvatarKind.LIBRARY && avatarSpec.configLibrary) {
    return avatarSpec.configLibrary;
  }
  if (selectedAvatarKind === AvatarKind.CUSTOM && avatarSpec.configCustom) {
    return avatarSpec.configCustom;
  }
  return avatarSpec.config;
};

/**
 * Gets the base config for a draft project based on selectedAvatarKind.
 * Returns the appropriate mode-specific config or defaults to normalized default config.
 */
export const getBaseConfigForDraft = (
  selectedAvatarKind: AvatarKind | null,
  configLibrary: AvatarSpecConfig | undefined,
  configCustom: AvatarSpecConfig | undefined
): AvatarSpecConfig => {
  if (selectedAvatarKind === AvatarKind.LIBRARY) {
    return configLibrary ?? normalizeAvatarSpecConfig(undefined);
  }
  if (selectedAvatarKind === AvatarKind.CUSTOM) {
    return configCustom ?? normalizeAvatarSpecConfig(undefined);
  }
  return normalizeAvatarSpecConfig(undefined);
};

// ============================================================================
// SCRIPT DATA UTILITIES
// ============================================================================

// ----------------------------------------------------------------------------
// Script Data: API → Store
// ----------------------------------------------------------------------------

/**
 * Maps a single script from API format to store format.
 * Since ScriptConfiguration is now unified, this just ensures valid values.
 */
export const mapScriptFromApi = (
  script: AiAvatarScriptDraft
): GeneratedScript => {
  return {
    title: script.title || '',
    script: script.script || '',
    inputScriptConfiguration:
      script.inputScriptConfiguration || DEFAULT_SCRIPT_CONFIG,
  } as GeneratedScript;
};

/**
 * Parses scriptData from API response format (validatedDraft.draft.scriptData).
 * Converts API format with unknown inputScriptConfiguration to store format with ScriptConfiguration.
 */
export const parseScriptDataFromApi = (
  scriptData: AiAvatarDraft['scriptData']
): DraftAiAvatarProject['scriptData'] => {
  if (!scriptData) {
    return {
      scripts: [],
      selectedScriptIndex: null,
    };
  }

  const scripts = (scriptData.scripts || []).map(mapScriptFromApi);
  const selectedScriptIndex =
    typeof scriptData.selectedScriptIndex === 'number'
      ? scriptData.selectedScriptIndex
      : null;

  return {
    scripts,
    selectedScriptIndex,
  };
};

// ----------------------------------------------------------------------------
// Script Data: Store → API
// ----------------------------------------------------------------------------

/**
 * Maps scriptData from store format to API format.
 * Since ScriptConfiguration is now unified, this mainly validates structure.
 * Validates and normalizes selectedScriptIndex to ensure it's within bounds.
 */
export const mapScriptDataToApiFormat = (
  scriptData: DraftAiAvatarProject['scriptData']
): AiAvatarDraft['scriptData'] => {
  const scripts = scriptData.scripts.map((script) => {
    return {
      title: script.title || '',
      script: typeof script.script === 'string' ? script.script : '',
      inputScriptConfiguration:
        script.inputScriptConfiguration || DEFAULT_SCRIPT_CONFIG,
    };
  });

  // Validate and normalize selectedScriptIndex
  const idx =
    typeof scriptData.selectedScriptIndex === 'number'
      ? scriptData.selectedScriptIndex
      : 0;
  const selectedScriptIndex = idx < 0 || idx >= scripts.length ? 0 : idx;

  return {
    scripts,
    selectedScriptIndex,
  };
};
